import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { TraTokenLink } from 'src/components/properties/tra-token-link'
import { isAxiosError } from 'axios'
import { toast } from 'react-toastify'
import i18next from 'i18next'
import { Modal } from 'src/components/modal'
import { Button } from 'src/components/button'
import { Input } from 'src/components/input'
import { Switch } from 'src/components/switch'
import { CustomSelect } from 'src/components/custom-select'
import { propertyApi } from 'src/services/properties/api'
import { propertyQueries } from 'src/services/properties/queries'
import type { Property } from 'src/services/properties/models'
import type { SelectOption } from 'src/services/base-model'
import { cn } from 'src/utils/style'
import { reportingGuidePath } from 'src/content/help'

const ACCOMMODATION_TYPE_VALUES = ['Apartamento', 'Casa', 'Hotel', 'Hostal'] as const

type WizardFormState = {
  traEnabled: boolean
  traApiKey: string
  traRntCode: string
  traEstablishmentName: string
  traAccommodationType: string
  sireEnabled: boolean
  sireEstablishmentCode: string
  sireCityCode: string | null
}

type SireCityState = 'resolved' | 'unresolved' | 'overridden'

function deriveSireCityState(
  sireCityCode: string | null,
  sireCityCodeResolved: string | null | undefined,
): SireCityState {
  if (sireCityCode) return 'overridden'
  if (sireCityCodeResolved) return 'resolved'
  return 'unresolved'
}

type CoSetupWizardProps = {
  isOpen: boolean
  onClose: () => void
  property: Property
}

export const CoSetupWizard = ({ isOpen, onClose, property }: CoSetupWizardProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [step, setStep] = useState(1)
  const [form, setForm] = useState<WizardFormState>({
    traEnabled: false,
    traApiKey: '',
    traRntCode: '',
    traEstablishmentName: '',
    traAccommodationType: '',
    sireEnabled: false,
    sireEstablishmentCode: '',
    sireCityCode: property.sireCityCode ?? null,
  })
  const [selectedCity, setSelectedCity] = useState<SelectOption[]>([])

  const cityState = deriveSireCityState(form.sireCityCode, property.sireCityCodeResolved)
  const sireToggleDisabled = cityState === 'unresolved'

  const { data: sireCitiesData, isLoading: loadingCities } = useQuery({
    ...propertyQueries.sireCities(),
    enabled: isOpen,
  })

  const cityOptions: SelectOption[] = useMemo(
    () =>
      (sireCitiesData?.cities ?? []).map((c) => ({
        label: `${c.name} (${c.code})`,
        value: c.code,
      })),
    [sireCitiesData],
  )

  const saveMutation = useMutation({
    mutationFn: () => {
      const data: Record<string, unknown> = {
        traEnabled: form.traEnabled,
        traRntCode: form.traRntCode || null,
        traEstablishmentName: form.traEstablishmentName || null,
        traAccommodationType: form.traAccommodationType || null,
        sireEnabled: form.sireEnabled,
        sireEstablishmentCode: form.sireEstablishmentCode || null,
        sireCityCode: form.sireCityCode,
      }
      if (form.traApiKey.trim().length > 0) {
        data.traApiKey = form.traApiKey.trim()
      }
      return propertyApi.update({ id: property.id, ...data })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: propertyQueries.all() })
      toast.success(t('reporting.saved'))
      onClose()
    },
    onError: (error: unknown) => {
      if (isAxiosError(error) && error.response?.status === 400 && error.response.data) {
        const data = error.response.data as Record<string, string[] | string>
        const firstKey = Object.keys(data)[0]
        if (firstKey) {
          const raw = data[firstKey]
          const msg = Array.isArray(raw) ? raw[0] : raw
          toast.error(`${firstKey}: ${msg}`)
          return
        }
      }
      toast.error(t('reporting.saveError'))
    },
  })

  const handleClose = () => {
    setStep(1)
    setForm({
      traEnabled: false,
      traApiKey: '',
      traRntCode: '',
      traEstablishmentName: '',
      traAccommodationType: '',
      sireEnabled: false,
      sireEstablishmentCode: '',
      sireCityCode: property.sireCityCode ?? null,
    })
    setSelectedCity([])
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('reporting.onboarding.wizardTitle')}
      size="lg"
    >
      <div data-testid="co-setup-wizard">
        {/* Progress indicator */}
        <div className="mb-6 flex items-center justify-center gap-2">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={cn([
                'h-2 w-2 rounded-full transition-colors',
                s === step ? 'bg-primary' : s < step ? 'bg-primary-200' : 'bg-gray-200',
              ])}
            />
          ))}
          <span className="ml-2 text-xs text-text-secondary">
            {t('reporting.onboarding.stepOf', { current: step, total: 3 })}
          </span>
        </div>

        {step === 1 && (
          <StepTra
            form={form}
            setForm={setForm}
            onNext={() => setStep(2)}
            onSkip={() => setStep(2)}
          />
        )}

        {step === 2 && (
          <StepSire
            form={form}
            setForm={setForm}
            property={property}
            cityState={cityState}
            sireToggleDisabled={sireToggleDisabled}
            cityOptions={cityOptions}
            selectedCity={selectedCity}
            setSelectedCity={setSelectedCity}
            loadingCities={loadingCities}
            sireCitiesData={sireCitiesData}
            onNext={() => setStep(3)}
            onSkip={() => setStep(3)}
            onBack={() => setStep(1)}
          />
        )}

        {step === 3 && (
          <StepSummary
            form={form}
            cityState={cityState}
            sireCitiesData={sireCitiesData}
            property={property}
            saveMutation={saveMutation}
            onBack={() => setStep(2)}
          />
        )}
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Step 1 — TRA
// ---------------------------------------------------------------------------

const StepTra = ({
  form,
  setForm,
  onNext,
  onSkip,
}: {
  form: WizardFormState
  setForm: React.Dispatch<React.SetStateAction<WizardFormState>>
  onNext: () => void
  onSkip: () => void
}) => {
  const { t } = useTranslation()

  return (
    <div>
      <h4 className="text-lg font-semibold text-text-primary">
        {t('reporting.onboarding.traStepTitle')}
      </h4>
      <p className="mt-2 text-sm text-text-secondary">{t('reporting.onboarding.traExplanation')}</p>
      <a
        href={reportingGuidePath(i18next.language, 'tra')}
        data-testid="wizard-guide-link"
        target="_blank"
        rel="noreferrer noopener"
        className="mt-2 inline-block text-xs text-primary underline hover:text-primary-400"
      >
        {t('reporting.howDoIGetThese')}
      </a>

      <div className="mt-4 flex items-center gap-3">
        <Switch
          checked={form.traEnabled}
          onCheckedChange={(checked) => setForm((p) => ({ ...p, traEnabled: checked }))}
          data-testid="wizard-tra-toggle"
        />
        <span className="text-sm text-text-primary">{t('reporting.enableTra')}</span>
      </div>

      {form.traEnabled && (
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <Input
              type="password"
              label={t('reporting.apiKeyLabel')}
              autoComplete="off"
              placeholder={t('reporting.apiKeyPlaceholder')}
              value={form.traApiKey}
              onChange={(e) => setForm((p) => ({ ...p, traApiKey: e.target.value }))}
              data-testid="wizard-tra-api-key"
            />
            <TraTokenLink />
          </div>
          <div>
            <Input
              label={t('reporting.rntLabel')}
              placeholder={t('reporting.rntPlaceholder')}
              value={form.traRntCode}
              onChange={(e) => setForm((p) => ({ ...p, traRntCode: e.target.value }))}
              data-testid="wizard-tra-rnt-code"
            />
            <p className="mt-1 text-xs text-text-placeholder">
              {t('reporting.onboarding.traRntHint')}
            </p>
          </div>
          <div>
            <Input
              label={t('reporting.establishmentNameLabel')}
              placeholder={t('reporting.establishmentNamePlaceholder')}
              value={form.traEstablishmentName}
              onChange={(e) => setForm((p) => ({ ...p, traEstablishmentName: e.target.value }))}
              data-testid="wizard-tra-establishment-name"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-primary">
              {t('reporting.accommodationTypeLabel')}
            </label>
            <select
              value={form.traAccommodationType}
              onChange={(e) => setForm((p) => ({ ...p, traAccommodationType: e.target.value }))}
              className="w-full rounded-md border bg-surface p-2 text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-strong"
              data-testid="wizard-tra-accommodation-type"
            >
              <option value="">{t('reporting.onboarding.accommodationTypeDefault')}</option>
              {ACCOMMODATION_TYPE_VALUES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <Button
          variant="text"
          tone="neutral"
          type="button"
          onClick={onSkip}
          className="text-sm underline"
        >
          {t('reporting.onboarding.skip')}
        </Button>
        <Button variant="primary" onClick={onNext} data-testid="wizard-next-1">
          {t('reporting.onboarding.next')}
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 2 — SIRE
// ---------------------------------------------------------------------------

const StepSire = ({
  form,
  setForm,
  property,
  cityState,
  sireToggleDisabled,
  cityOptions,
  selectedCity,
  setSelectedCity,
  loadingCities,
  sireCitiesData,
  onNext,
  onSkip,
  onBack,
}: {
  form: WizardFormState
  setForm: React.Dispatch<React.SetStateAction<WizardFormState>>
  property: Property
  cityState: SireCityState
  sireToggleDisabled: boolean
  cityOptions: SelectOption[]
  selectedCity: SelectOption[]
  setSelectedCity: React.Dispatch<React.SetStateAction<SelectOption[]>>
  loadingCities: boolean
  sireCitiesData: { cities: { code: string; name: string }[] } | undefined
  onNext: () => void
  onSkip: () => void
  onBack: () => void
}) => {
  const { t } = useTranslation()

  const handleCitySelect = (values: SelectOption[]) => {
    setSelectedCity(values)
    if (values.length > 0) {
      setForm((p) => ({ ...p, sireCityCode: values[0].value }))
    }
  }

  return (
    <div>
      <h4 className="text-lg font-semibold text-text-primary">
        {t('reporting.onboarding.sireStepTitle')}
      </h4>
      <p className="mt-2 text-sm text-text-secondary">
        {t('reporting.onboarding.sireExplanation')}
      </p>

      {/* City resolution */}
      <div className="mt-4" data-testid="wizard-sire-city-status">
        {cityState === 'resolved' && (
          <div className="flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green">
            <span>
              {t('reporting.sireCityResolved', {
                name: property.sireCityNameResolved,
                code: property.sireCityCodeResolved,
              })}
            </span>
          </div>
        )}
        {cityState === 'overridden' && (
          <div className="flex items-center gap-2 rounded-md bg-accent-50 px-3 py-2 text-sm text-accent">
            <span>
              {t('reporting.sireCityOverridden', {
                name:
                  sireCitiesData?.cities?.find(
                    (c: { code: string }) => c.code === form.sireCityCode,
                  )?.name ?? property.sireCityNameResolved,
                code: form.sireCityCode ?? property.sireCityCodeResolved,
              })}
            </span>
          </div>
        )}
        {cityState === 'unresolved' && (
          <div>
            <p className="mb-2 rounded-md bg-error-50 px-3 py-2 text-sm text-error">
              {t('reporting.sireCityUnresolved')}
            </p>
            <CustomSelect
              searchable
              clearable
              options={cityOptions}
              values={selectedCity}
              loading={loadingCities}
              placeholder={t('reporting.sireCityPickerPlaceholder')}
              onChange={handleCitySelect}
              data-testid="wizard-sire-city-picker"
            />
          </div>
        )}
      </div>

      {/* SIRE toggle */}
      <div className="mt-4 flex items-center gap-3">
        <Switch
          checked={form.sireEnabled}
          onCheckedChange={(checked) => setForm((p) => ({ ...p, sireEnabled: checked }))}
          disabled={sireToggleDisabled}
          data-testid="wizard-sire-toggle"
        />
        <span className={cn(['text-sm text-text-primary', sireToggleDisabled && 'opacity-50'])}>
          {t('reporting.enableSire')}
        </span>
      </div>
      {sireToggleDisabled && (
        <p className="mt-1 text-xs text-text-tertiary">{t('reporting.sireEnableBlockedTooltip')}</p>
      )}

      {form.sireEnabled && (
        <div className="mt-3">
          <Input
            label={t('reporting.sireCodeLabel')}
            placeholder={t('reporting.sireCodePlaceholder')}
            value={form.sireEstablishmentCode}
            onChange={(e) => setForm((p) => ({ ...p, sireEstablishmentCode: e.target.value }))}
            data-testid="wizard-sire-establishment-code"
          />
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} data-testid="wizard-back-2">
            {t('reporting.onboarding.back')}
          </Button>
          <Button
            variant="text"
            tone="neutral"
            type="button"
            onClick={onSkip}
            className="text-sm underline"
          >
            {t('reporting.onboarding.skip')}
          </Button>
        </div>
        <Button variant="primary" onClick={onNext} data-testid="wizard-next-2">
          {t('reporting.onboarding.next')}
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 3 — Summary
// ---------------------------------------------------------------------------

const StepSummary = ({
  form,
  cityState: _cityState,
  sireCitiesData,
  property,
  saveMutation,
  onBack,
}: {
  form: WizardFormState
  cityState: SireCityState
  sireCitiesData: { cities: { code: string; name: string }[] } | undefined
  property: Property
  saveMutation: ReturnType<typeof useMutation<unknown, unknown, void>>
  onBack: () => void
}) => {
  const { t } = useTranslation()

  const sireCityName = (() => {
    if (form.sireCityCode) {
      return (
        sireCitiesData?.cities?.find((c) => c.code === form.sireCityCode)?.name ?? form.sireCityCode
      )
    }
    return property.sireCityNameResolved ?? null
  })()

  return (
    <div>
      <h4 className="text-lg font-semibold text-text-primary">
        {t('reporting.onboarding.summaryStepTitle')}
      </h4>

      <div className="mt-4 space-y-3">
        {/* TRA summary */}
        <div className="rounded-md border border-border p-3">
          <div className="flex items-center gap-2">
            <span
              className={cn([
                'inline-block h-2 w-2 rounded-full',
                form.traEnabled ? 'bg-green-400' : 'bg-gray-300',
              ])}
            />
            <span className="text-sm font-medium text-text-primary">
              {t('reporting.onboarding.traLabel')}
            </span>
            <span
              className={cn([
                'ml-auto text-xs font-medium',
                form.traEnabled ? 'text-green' : 'text-text-tertiary',
              ])}
            >
              {form.traEnabled
                ? t('reporting.onboarding.enabled')
                : t('reporting.onboarding.skipped')}
            </span>
          </div>
          {form.traEnabled && (
            <ul className="mt-2 space-y-1 text-xs text-text-secondary">
              {form.traRntCode && (
                <li>
                  {t('reporting.rntLabel')}: {form.traRntCode}
                </li>
              )}
              {form.traEstablishmentName && (
                <li>
                  {t('reporting.establishmentNameLabel')}: {form.traEstablishmentName}
                </li>
              )}
              {form.traAccommodationType && (
                <li>
                  {t('reporting.accommodationTypeLabel')}: {form.traAccommodationType}
                </li>
              )}
              {form.traApiKey && <li>{t('reporting.apiKeyLabel')}: ****</li>}
            </ul>
          )}
        </div>

        {/* SIRE summary */}
        <div className="rounded-md border border-border p-3">
          <div className="flex items-center gap-2">
            <span
              className={cn([
                'inline-block h-2 w-2 rounded-full',
                form.sireEnabled ? 'bg-green-400' : 'bg-gray-300',
              ])}
            />
            <span className="text-sm font-medium text-text-primary">
              {t('reporting.onboarding.sireLabel')}
            </span>
            <span
              className={cn([
                'ml-auto text-xs font-medium',
                form.sireEnabled ? 'text-green' : 'text-text-tertiary',
              ])}
            >
              {form.sireEnabled
                ? t('reporting.onboarding.enabled')
                : t('reporting.onboarding.skipped')}
            </span>
          </div>
          {form.sireEnabled && (
            <ul className="mt-2 space-y-1 text-xs text-text-secondary">
              {form.sireEstablishmentCode && (
                <li>
                  {t('reporting.sireCodeLabel')}: {form.sireEstablishmentCode}
                </li>
              )}
              {sireCityName && (
                <li>
                  {t('reporting.onboarding.sireCity')}: {sireCityName}
                </li>
              )}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} data-testid="wizard-back-3">
          {t('reporting.onboarding.back')}
        </Button>
        <Button
          variant="primary"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          data-testid="wizard-save"
        >
          {saveMutation.isPending ? t('reporting.saving') : t('reporting.onboarding.saveAndFinish')}
        </Button>
      </div>
    </div>
  )
}
